import { useRef, useEffect, useState } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";

import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { About } from "@/components/About";
import { Pillars } from "@/components/Pillars";
import { Community } from "@/components/Community";
import { ContactSection } from "@/components/ContactSection";
import { Footer } from "@/components/Footer";
import { IntroReel } from "@/components/IntroReel";
import StorePage from "@/pages/StorePage";
import VoicesPage from "@/pages/VoicesPage";
import FamilyPage from "@/pages/FamilyPage";
import ShowcaseRegisterPage from "@/pages/ShowcaseRegisterPage";
import BudgetPage from "@/pages/BudgetPage";
import PartnershipPage from "@/pages/PartnershipPage";
import EmploymentPage from "@/pages/EmploymentPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import CheckoutCancelPage from "@/pages/CheckoutCancelPage";
import AdminEmploymentPage from "@/pages/AdminEmploymentPage";
import AdminProfilePage from "@/pages/AdminProfilePage";
import AdminCoachOfficialPage from "@/pages/AdminCoachOfficialPage";
import AdminTeamOwnersPage from "@/pages/AdminTeamOwnersPage";
import CoachOfficialRegisterPage from "@/pages/CoachOfficialRegisterPage";
import TeamOwnerRegisterPage from "@/pages/TeamOwnerRegisterPage";
import { CartProvider } from "@/lib/CartContext";
import { CartSheet } from "@/components/CartSheet";
import { AuthProvider } from "@/lib/AuthContext";

// Scroll to hash element when navigating between pages
const ScrollToHash = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      setTimeout(() => {
        document
          .getElementById(id)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, hash]);
  return null;
};

const Landing = () => {
  const contactRef = useRef(null);
  const [introDone, setIntroDone] = useState(() => {
    if (typeof window === "undefined") return true;
    return sessionStorage.getItem("afroam_intro_seen") === "1";
  });

  const handleIntroDone = () => {
    sessionStorage.setItem("afroam_intro_seen", "1");
    setIntroDone(true);
  };

  // Prevent body scroll while intro is playing
  useEffect(() => {
    if (!introDone) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [introDone]);

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToPrograms = () => {
    document
      .getElementById("programs")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="App bg-black text-white">
      {!introDone && <IntroReel onDone={handleIntroDone} />}
      <Navbar onCtaClick={scrollToContact} />
      <Hero
        onPrimaryClick={scrollToContact}
        onSecondaryClick={scrollToPrograms}
      />
      <About />
      <Pillars />
      <Community />
      <ContactSection formRef={contactRef} />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToHash />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/store" element={<StorePage />} />
            <Route path="/voices" element={<VoicesPage />} />
            <Route path="/family" element={<FamilyPage />} />
            <Route path="/showcase/register" element={<ShowcaseRegisterPage />} />
            <Route path="/budget" element={<BudgetPage />} />
            <Route path="/partnerships" element={<PartnershipPage />} />
            <Route path="/employment" element={<EmploymentPage />} />
            <Route path="/careers" element={<EmploymentPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
            <Route path="/admin/employment" element={<AdminEmploymentPage />} />
            <Route path="/admin/profile" element={<AdminProfilePage />} />
            <Route path="/admin/coaches-officials" element={<AdminCoachOfficialPage />} />
            <Route path="/admin/team-owners" element={<AdminTeamOwnersPage />} />
            <Route path="/coaches-officials" element={<CoachOfficialRegisterPage />} />
            <Route path="/coaches-officials/register" element={<CoachOfficialRegisterPage />} />
            <Route path="/team-owners" element={<TeamOwnerRegisterPage />} />
            <Route path="/ownership" element={<TeamOwnerRegisterPage />} />
          </Routes>
          <CartSheet />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#0a0a0a",
                border: "1px solid #262626",
                color: "#fff",
                borderRadius: 0,
                fontFamily: "Inter, sans-serif",
                letterSpacing: "0.05em",
              },
            }}
          />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
